import { Role, User, UserType } from '@nemuru-eng/js-api-client';
import { useAuth } from '@nemuru-eng/providers';
import { useEffect } from 'react';

import {
  defaultNavMenu,
  iberiaCardsApplicationsMenu,
  iberiaCardsSalesMenu,
  newApplicationMenu,
} from '@/components/views/DynamicPageView/config/layoutScheme/navMenuSchemes/defaultNavMenu';
import { navMenuSchemesByUserType, pageSchemes } from '@/components/views/DynamicPageView/config/layoutScheme/schemes';
import { DYNAMIC_PAGE_PATHS } from '@/components/views/DynamicPageView/config/shared/pathRoutes';
import {
  AppRole,
  Layout,
  Link,
  NavSection,
  RoleBasedScheme,
  SectionScheme,
  UserTypeBasedScheme,
  Widget,
} from '@/config/types';
import { useConfigContext } from '@/contexts/ConfigContext/ConfigContext';
import { useUserContext } from '@/contexts/UserContext/UserContext';
import { UserEntity } from '@/entities/UserEntity';
import { applyIberiaRestrictedUserLayout } from '@/hooks/layoutConstructor/iberiaLayoutConstructorHelpers';
import { isIbcSalesRestrictedUser, isIbcStandsAgentUser } from '@/hooks/layoutConstructor/iberiaUserHelpers';
import {
  getClonedTeslaMerchantChannelAgentNavMenu,
  isTeslaMerchantChannelAgent,
  removePagesFromTeslaLayout,
} from '@/hooks/layoutConstructor/teslaLayoutConstructorHelpers';

export const getSchemeByRole = <T extends RoleBasedScheme>(
  schemesByRoles: T[] | undefined,
  role?: AppRole,
): T | undefined => {
  if (!schemesByRoles || !role) return undefined;
  return schemesByRoles.find((schemeByRole) => schemeByRole.roles && schemeByRole.roles.includes(role));
};

export const getNavMenuByUserType = <T extends UserTypeBasedScheme>(
  schemesByUserTypes: T[] | undefined,
  userType?: UserType,
): T | undefined => {
  if (!schemesByUserTypes || !userType) return undefined;
  return schemesByUserTypes.find(
    (schemeByUserType) => schemeByUserType.userTypes && schemeByUserType.userTypes.includes(userType),
  );
};

// FIXME : Incorporate new permissions to LayoutMap
export const constructSectionsByPageSchemeAndRole = (): SectionScheme[] => {
  const newSections: SectionScheme[] = [];
  // pageScheme.sections.forEach((section) => {
  //   const sectionScheme = getSchemeByRole(section.schemesByRoles, role);
  //   if (sectionScheme) {
  //     newSections.push({
  //       textId: section.textId,
  //       modules: sectionScheme.modules,
  //     });
  //   }
  // });
  return newSections;
};

const allowedWidgetsByRole = (role?: AppRole, widgets?: Widget[]): Widget[] | undefined => {
  if (!widgets || !role) return;
  return widgets.filter((widget) => widget.roles.includes(role));
};

export function useLayoutConstructor() {
  const { roleOverride, appStarted, userEntity } = useUserContext();

  const { user } = useAuth();
  const { actions: configActions } = useConfigContext();

  const role = roleOverride || userEntity?.role;

  useEffect(() => {
    if (appStarted) {
      const layout = constructLayout();
      if (layout) {
        configActions.setCurrentLayout(layout);
      }
    }
  }, [appStarted]);

  const constructLayout = (): Layout | undefined => {
    if (!user || !userEntity) return;

    const navMenuScheme = getNavMenuByUserType(navMenuSchemesByUserType, userEntity.user.userType);

    if (!navMenuScheme) return;

    if (userEntity.merchant && IBERIA_CARDS_MERCHANT_IDS.includes(userEntity.merchant.id)) {
      const iberiaNavSections: NavSection[] = [];
      if (isIbcStandsAgentUser(userEntity)) {
        iberiaNavSections.push(newApplicationMenu, iberiaCardsSalesMenu);

        let newLayout: Layout = {
          navSections: iberiaNavSections,
          pagesByPath: {},
          showSuperSearchBar: false,
        };
        newLayout = addPagesToLayout(newLayout, userEntity, role);
        delete newLayout.pagesByPath[DYNAMIC_PAGE_PATHS.CREDIT_CARD_LIST];

        return newLayout;
      }

      if (userEntity.isRoleMerchantChannelOrAgent()) {
        if (!isIbcSalesRestrictedUser(userEntity)) {
          iberiaNavSections.push(iberiaCardsSalesMenu);
        }

        if (!userEntity.hasRole([Role.ROLE_IBC_TLMK_AGENT, Role.ROLE_IBC_TLMK_CHANNEL])) {
          iberiaNavSections.push(iberiaCardsApplicationsMenu);
        }
      }

      let newLayout: Layout = {
        navSections: iberiaNavSections,
        pagesByPath: {},
        showSuperSearchBar: false,
      };
      newLayout = addPagesToLayout(newLayout, userEntity, role);
      newLayout = applyIberiaRestrictedUserLayout(newLayout, userEntity);
      return newLayout;
    }

    const isTeslaMerchantChannelAgentNavUser = isTeslaMerchantChannelAgent(userEntity, TESLA_MERCHANT_IDS);

    // FIX FOR LEGACY MERCHANT GROUP USERS
    if (
      userEntity.hasUserType([UserType.MERCHANT_GROUP]) &&
      LEGACY_MERCHANT_GROUP_USER_IDS.includes(userEntity.user.userId)
    ) {
      // FIXME: We should filter types of applications based on allowed by all agents
      // For now, we will hardcode it here...
      navMenuScheme.navMenu = defaultNavMenu.map((menu) => {
        if (menu.nameTextId === 'APPLICATIONS') {
          return {
            ...menu,
            links: menu.links.filter((link) => link.path === DYNAMIC_PAGE_PATHS.ORDER_LIST),
          };
        }
        return menu;
      });
    }

    // FIX FOR LEGACY MERCHANT AGENTS
    if (
      userEntity.hasUserType([UserType.AGENT]) &&
      userEntity.merchant &&
      LEGACY_MERCHANT_IDS.includes(userEntity.merchant.id)
    ) {
      navMenuScheme.navMenu = [];
      navMenuScheme.showSuperSearchBar = false;
    }

    // TEMPORARY FIX FOR TESLA MERCHANT NAV MENU
    if (isTeslaMerchantChannelAgentNavUser) {
      navMenuScheme.navMenu = getClonedTeslaMerchantChannelAgentNavMenu();
    }

    let newLayout: Layout = {
      navSections: navMenuScheme.navMenu,
      pagesByPath: {},
      showSuperSearchBar: navMenuScheme.showSuperSearchBar,
    };

    newLayout = addPagesToLayout(newLayout, userEntity, role);

    if (isTeslaMerchantChannelAgentNavUser && userEntity.merchant) {
      removePagesFromTeslaLayout(newLayout, userEntity.merchant.id, TESLA_MERCHANT_IDS);
    }

    return filterLayoutByRole(removeUnusedNavItems(newLayout), role, user);
  };
}

const addPagesToLayout = (layout: Layout, userEntity: UserEntity, role?: Role): Layout => {
  pageSchemes.forEach((pageScheme) => {
    if (!pageScheme.showIfAllowedProduct || !!userEntity.allowedProducts.includes(pageScheme.showIfAllowedProduct)) {
      const pageOptionsScheme = getSchemeByRole(pageScheme.schemesByRoles, role);
      if (pageOptionsScheme) {
        const widgets = allowedWidgetsByRole(role, pageOptionsScheme.widgets);
        // const sections = constructSectionsByPageSchemeAndRole(pageScheme, role);
        const sections = pageScheme.sections;
        layout.pagesByPath[pageScheme.path] = {
          ...pageOptionsScheme,
          widgets,
          sections,
        };
      }
    }
  });
  return layout;
};

const removeUnusedNavItems = (layout: Layout): Layout => {
  const newNavSections = [] as NavSection[];
  layout.navSections.forEach((section) => {
    const newLinks = [] as Link[];
    section.links.forEach((link) => {
      const existsPageOfLinkPath = layout.pagesByPath[link.path];
      if (existsPageOfLinkPath) {
        newLinks.push(link);
      }
    });
    section.links = newLinks;
    if (section.links.length !== 0) {
      newNavSections.push(section);
    }
  });
  layout.navSections = newNavSections;
  return layout;
};

// FIXME: Temporary function to remove specific sections from layout based on role
const filterLayoutByRole = (layout: Layout, role: AppRole | undefined, user: User): Layout => {
  if (role !== Role.ROLE_LENDER) return layout;
  return {
    ...layout,
    navSections: [...layout.navSections].map((navSection) => {
      if (navSection.nameTextId !== 'PRODUCTS') return navSection;
      if (user.username.includes('upf.lender')) {
        return {
          ...navSection,
          links: [...navSection.links].filter((link) => link.path === DYNAMIC_PAGE_PATHS.CONSUMER_LOAN_LIST),
        };
      }
      if (user.username.includes('astara.lender')) {
        return {
          ...navSection,
          links: [...navSection.links].filter((link) => link.path === DYNAMIC_PAGE_PATHS.AUTO_LOAN_LIST),
        };
      }
      return navSection;
    }),
  };
};

const LEGACY_MERCHANT_GROUP_USER_IDS = [
  // Production
  '78bfba7e-98b2-475a-ad90-cad07b4470d4', // Avoris
  'f486b75a-678f-4ce5-ad17-611ecfaab7df', // W2M
  // Staging
  '29ae85aa-0441-43f2-825b-899a38c3dad1', // Iberia
  '99b28a15-a48d-429d-99ad-d3700e20c261', // Avoris
  'd82e7aa5-2dbc-4f4b-9eb9-813d63e46a33', // Avoris
];

export const LEGACY_MERCHANT_IDS = [
  '52315d99-be63-445b-a8dc-787e776b76e8', // IKEA STG
  '0b0a371c-e993-43d5-8f55-8551ca61daa9', // IKEA STG
  '02578465-4679-4f8e-9b15-05390890324d', // IKEA PROD
];

const IBERIA_CARDS_MERCHANT_IDS = [
  '21e515a9-a7f0-49b0-b77e-7e2f37a85f47', // Dev
  'aa9d7b39-fa58-47a2-b3d9-0714b638b466', // Dev (new merchant to develop Iberia telemarketing)
  '10690017-62a5-4ec9-8ae6-194b42f2953d', // Staging
  '48f3238a-1dbb-4d2a-8511-abfed009bf95', // Prod
];

const TESLA_MERCHANT_IDS: readonly string[] = [
  '8cf7daed-e4bb-4ad8-91ba-d3bf50117478', // TESLA MERCHANT DEV
  '9704ea01-33a5-4d10-80eb-6a644785330d', // TESLA MERCHANT STAGING
  '76d3726d-87b5-40a4-acd0-0337daf7b414', // TESLA MERCHANT PRODUCTION
];
